import React, { useState } from 'react';
import { APIProvider } from '@vis.gl/react-google-maps';
import { MapPin, Globe, Building2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import BusinessSearchCard from './BusinessSearchCard';
import PlacesAutocomplete from './PlacesAutocomplete';
import AdvertiserSelector from './organization/AdvertiserSelector';

const SEARCH_TABS = [
  { id: 'search', label: 'Google Maps', icon: MapPin },
  { id: 'website', label: 'Website URL', icon: Globe },
];

const isValidUrl = (value) => {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

export default function BusinessSearchExperience({
  title,
  subtitle,
  searchMethod,
  setSearchMethod,
  selectedPlaceId,
  selectedBusiness,
  isLoading = false,
  error = null,
  currentOrg = null,
  selectedAdvertiserId = null,
  setSelectedAdvertiserId,
  onPlaceSelect,
  onSubmitSource,
  ctaLabel,
  ctaLoadingLabel,
  footerSlot = null,
  searchPlaceholder = 'Enter business name...',
  websitePlaceholder = 'https://mybusiness.com',
}) {
  const [websiteUrl, setWebsiteUrl] = useState('');

  const hasSource = searchMethod === 'search'
    ? !!selectedPlaceId
    : isValidUrl(websiteUrl);
  const needsAdvertiser = !!currentOrg && !selectedAdvertiserId && hasSource;

  const handleSubmit = () => {
    if (!hasSource || needsAdvertiser) return;

    if (searchMethod === 'search') {
      onSubmitSource?.({ place_id: selectedPlaceId });
      return;
    }

    onSubmitSource?.({ website_url: websiteUrl.trim() });
  };

  const disabledTooltip = needsAdvertiser
    ? 'Please select an advertiser account to proceed'
    : (!hasSource
      ? (searchMethod === 'search'
        ? 'Search and select a business'
        : 'Enter a valid website URL')
      : null);

  return (
    <BusinessSearchCard
      title={title}
      subtitle={subtitle}
      tabs={SEARCH_TABS}
      activeTab={searchMethod}
      onTabChange={setSearchMethod}
      error={error}
      ctaLabel={ctaLabel}
      ctaLoadingLabel={ctaLoadingLabel}
      ctaDisabled={!hasSource || needsAdvertiser}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      continuationLabel={searchMethod === 'search' && selectedBusiness?.name
        ? `Continue as ${selectedBusiness.name}`
        : null}
      disabledTooltip={disabledTooltip}
      secondarySlot={
        currentOrg ? (
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold ml-1">
              Assign Advertiser
            </Label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none">
                <Building2 className="size-4" />
              </div>
              <div className="[&_button]:h-14 [&_button]:pl-11 [&_button]:rounded-xl [&_button]:text-base [&_button]:shadow-sm">
                <AdvertiserSelector
                  value={selectedAdvertiserId}
                  onChange={setSelectedAdvertiserId}
                  placeholder="Select advertiser account..."
                />
              </div>
            </div>
          </div>
        ) : null
      }
      showSecondarySlot={!!(currentOrg && hasSource)}
      footerSlot={footerSlot}
    >
      {searchMethod === 'search' && (
        <div className="relative group">
          <div className="w-full">
            <APIProvider
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
              version="weekly"
              loading="async"
            >
              <div className="relative">
                <PlacesAutocomplete
                  onPlaceSelect={onPlaceSelect}
                  placeholder={searchPlaceholder}
                  className="flex h-12 md:h-14 w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 shadow-sm transition-shadow group-hover:shadow-md"
                />
              </div>
            </APIProvider>
          </div>
        </div>
      )}

      {searchMethod === 'website' && (
        <div className="relative group">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground">
            <Globe className="size-5" />
          </div>
          <input
            type="url"
            className="flex h-12 md:h-14 w-full rounded-xl border border-input bg-background pl-12 pr-4 text-base shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-coral/20 focus:border-coral"
            placeholder={websitePlaceholder}
            value={websiteUrl}
            onChange={(event) => setWebsiteUrl(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSubmit();
              }
            }}
            aria-label="Business website URL"
          />
        </div>
      )}
    </BusinessSearchCard>
  );
}
